import { Link, usePage } from '@inertiajs/react';
import classNames from 'classnames';
import { ChangeEvent, useState } from 'react';
import { Image, Trash, XCircle } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../Shared/api';
import { initFieldEntriesData } from '../../Shared/helpers';
import ImageCropper from '../../Shared/Image/ImageCropper';
import { useLocale } from '../../Shared/locale';
import { useNotifier } from '../../Shared/Notifier/NotificationProvider';
import { AddressFields, Campaign, CampaignField, CampaignType, Charity, CustomField, CustomFieldChoice, CustomFieldChoiceEntry, CustomFieldEntry, DateOfBirthFields, Event, Gender, GenderFields, PhoneFields, Project, ProjectType, User } from '../../Shared/types';
import AddressInputs from '../../Shared/UI/AddressInputs';
import { Checkbox } from '../../Shared/UI/Checkbox';
import CheckboxLabel from '../../Shared/UI/CheckboxLabel';
import { DateInput } from '../../Shared/UI/DateInput';
import ErrorMessage from '../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../Shared/UI/FadeIn';
import { FileInput } from '../../Shared/UI/FileInput';
import { Icon } from '../../Shared/UI/Icon';
import InfoMessage from '../../Shared/UI/InfoMessage';
import { Input } from '../../Shared/UI/Input';
import InputDescription from '../../Shared/UI/InputDescription';
import InputGroup from '../../Shared/UI/InputGroup';
import InputLabel from '../../Shared/UI/InputLabel';
import { InputWrapper } from '../../Shared/UI/InputWrapper';
import PhoneNumberInput from '../../Shared/UI/PhoneNumberInput';
import { Radio } from '../../Shared/UI/Radio';
import RadioLabel from '../../Shared/UI/RadioLabel';
import ScrollIntoView from '../../Shared/UI/ScrollIntoView';
import { SearchFilterInput } from '../../Shared/UI/SearchFilterInput';
import ServerErrors from '../../Shared/UI/ServerErrors';
import { Textarea } from '../../Shared/UI/Textarea';
import { useCurrencyInput } from '../../Shared/useCurrencyInput';
import { useDateInput } from '../../Shared/useDateInput';
import useStickyElement from '../../Shared/useStickyElement';
import { CustomFieldInput, CustomFieldInputFormApi } from '../CustomFields/CustomFieldInput';
import { useRouter } from '../router';
import { PageProps } from '../types';
import { Button } from '../UI/Button';

interface EditCampaignFormProps {
  campaign: Campaign & AddressFields & GenderFields & DateOfBirthFields & PhoneFields & {
    project: Project & {
      custom_fields: (CustomField & { choices: CustomFieldChoice[]; })[];
    };
    event: Event | null;
    team: Campaign | null;
    custom_field_entries: (CustomFieldEntry & { choice_entries: CustomFieldChoiceEntry[]; })[];
    user: User;
  };
  teams: Campaign[];
  charity: Charity;
  scrollToEmailSettings?: boolean;
}

export function EditCampaignForm({
  campaign,
  teams,
  charity,
  scrollToEmailSettings = false,
}: EditCampaignFormProps) {
  const { t } = useTranslation();
  const { handleCurrencyBlur, formatCurrency, parseCurrency } = useCurrencyInput();
  const { handleDateBlur } = useDateInput();
  const { routes } = useRouter();
  const { defaultCountry, formatDate, parseDate } = useLocale();
  const { params } = usePage<PageProps>().props;
  const notifier = useNotifier();

  const project = campaign.project;

  const form = useForm({
    defaultValues: {
      title: campaign.title || '',
      image: null,
      delete_image: !campaign.image_url,
      start_date: campaign.start_date ? formatDate(campaign.start_date, 'internal_date') : null,
      end_date: campaign.end_date ? formatDate(campaign.end_date, 'internal_date') : null,
      fundraising_target: campaign.fundraising_target ? formatCurrency(campaign.fundraising_target) : '',
      description: campaign.description,
      donation_message: campaign.donation_message || '',
      team_id: campaign.team?.id || null,
      join_team: !!campaign.team,
      teams_search: typeof params.teams_search === 'string' ? params.teams_search : '',
      first_name: campaign.first_name || '',
      last_name: campaign.last_name || '',
      email: campaign.user.email || '',
      gender: campaign.gender,
      date_of_birth: campaign.date_of_birth ? formatDate(campaign.date_of_birth, 'input_date') : '',
      company_name: campaign.company_name || '',
      phone: campaign.phone || '',
      street: campaign.street || '',
      house_number: campaign.house_number || '',
      extra_address_line: campaign.extra_address_line || '',
      zip_code: campaign.zip_code || '',
      city: campaign.city || '',
      country: campaign.country || defaultCountry || '',
      custom_field_entries: initFieldEntriesData(campaign.custom_field_entries, project.custom_fields),
      email_opt_in: campaign.email_opt_in,
      private: campaign.private,
    },
  });

  const {
    register, control, setValue, handleSubmit, setError, resetField, watch,
    formState: { isSubmitting, errors, defaultValues, isDirty },
  } = form;

  const joinTeam = watch('join_team');
  const allowJoinTeam = campaign.type === CampaignType.Individual && ! campaign.locked_fields?.includes('team') && project.allow_teams;
  const allowLeaveTeam = campaign.type === CampaignType.Individual && ! campaign.locked_fields?.includes('team') && project.allow_individuals;
  const [selectedTeam, setSelectedTeam] = useState(campaign.team);

  const gender = watch('gender');

  const [image, setImage] = useState<File>();
  const [croppedImage, setCroppedImage] = useState<File>();
  const deleteImage = watch('delete_image');
  const canDeleteImage = !deleteImage && (!!image || !!campaign.image_url);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setImage(event.target.files?.[0] || undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', false, { shouldDirty: true });
  };

  const handleCropChange = (file: File, element: HTMLImageElement) => {
    if (element.naturalWidth < 800 || element.naturalHeight < 450) {
      resetImage();
      setError('image', { message: t('shared:validation.image_too_small') });
    } else {
      setCroppedImage(file);
    }
  };

  const resetImage = () => {
    setImage(undefined);
    setCroppedImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', true, { shouldDirty: true });
  };

  const startDate = watch('start_date');
  const endDate = watch('end_date');
  const email = watch('email');

  const [isCompany, setIsCompany] = useState(
    project.required_fields.includes(CampaignField.CompanyName) || !!campaign.company_name,
  );
  const toggleCompany = () => setIsCompany((isCompany) => !isCompany);

  const [editCampaign, { errors: serverErrors }] = useMutation(routes.edit_campaign(campaign.id), {
    onSuccess: () => {
      notifier.notify(t('frontend:campaign_saved'));
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  const stickyFooter = useStickyElement({
    position: 'bottom',
  });

  const submit = async (data: typeof defaultValues) => {
    const dateOfBirth = data?.date_of_birth && parseDate(data.date_of_birth, 'input_date');

    await editCampaign({
      title: data?.title,
      image: (image && croppedImage) || null,
      delete_image: data?.delete_image,
      start_date: data?.start_date,
      end_date: data?.end_date,
      fundraising_target: data?.fundraising_target ? parseCurrency(data.fundraising_target) : null,
      description: data?.description,
      donation_message: data?.donation_message,
      team_id: data?.join_team ? selectedTeam?.id : null,
      first_name: data?.first_name,
      last_name: data?.last_name,
      email: data?.email,
      gender: data?.gender,
      date_of_birth: dateOfBirth ? formatDate(dateOfBirth, 'internal_date') : null,
      company_name: isCompany ? data?.company_name : null,
      phone: data?.phone,
      street: data?.street,
      house_number: data?.house_number,
      extra_address_line: data?.extra_address_line,
      zip_code: data?.zip_code,
      city: data?.city,
      country: data?.country,
      custom_field_entries: data?.custom_field_entries,
      email_opt_in: data?.email_opt_in,
      private: data?.private,
    });
  };

  const allowStartDate = !joinTeam && project.fields.includes(CampaignField.StartDate);
  const allowEndDate = !joinTeam && project.fields.includes(CampaignField.EndDate);
  const allowCompanyName = !joinTeam && project.fields.includes(CampaignField.CompanyName);

  return (
    <form onSubmit={handleSubmit(submit)} className="space-y-8 sm:space-y-10">
      {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />}

      <div className="space-y-6">
        <h4 className="font-medium text-slate-500">
          {t('frontend:about_the_campaign')}
        </h4>

        {allowJoinTeam && (
          <>
            {allowLeaveTeam && (
              <InputGroup>
                <InputLabel htmlFor="edit_campaign_no_team" required valid>
                  {t('frontend:team')}
                </InputLabel>
                <RadioLabel>
                  <Radio
                    checked={!joinTeam}
                    onChange={(event) => setValue('join_team', !event.target.checked, { shouldDirty: true })}
                    id="edit_campaign_no_team"
                  />
                  <span>
                    {t('frontend:no_team')}
                  </span>
                </RadioLabel>
                <RadioLabel>
                  <Radio
                    checked={joinTeam}
                    onChange={(event) => setValue('join_team', event.target.checked, { shouldDirty: true })}
                  />
                  <span>
                    {t('frontend:join_team')}
                  </span>
                </RadioLabel>
              </InputGroup>
            )}

            <FadeIn
              when={joinTeam}
              render={() => (
                <InputGroup>
                  <InputLabel required valid={!errors.team_id && !errors.teams_search} htmlFor="teams_search">
                    {t('frontend:join_team')}
                  </InputLabel>
                  {!selectedTeam && (
                    <>
                      <InputDescription>
                        <Trans i18nKey="frontend:create_new_team_if_not_exists_description">
                          <Link href={routes.create_campaign_page(charity.id, project.type, {
                            projectId: campaign.event ? null : project.id,
                            eventId: campaign.event?.id,
                            memberId: campaign.id,
                            type: 'team',
                          })} />
                        </Trans>
                      </InputDescription>
                      <Controller
                        control={control}
                        name="teams_search"
                        rules={{ required: true }}
                        render={({ field: { onChange } }) => (
                          <SearchFilterInput name="teams_search" id="teams_search" onChange={onChange} />
                        )}
                      />
                      {teams.length > 0 && (
                        <div className="mt-3 space-y-1">
                          {teams.map((team) => (
                            <RadioLabel className="px-3 py-2 border rounded-md border-slate-250 hover:border-slate-300" key={team.id}>
                              <Radio
                                {...register('team_id', {
                                  onChange: (event) => setSelectedTeam(teams.find(team => team.id === event.target.value) || null),
                                  required: true,
                                })}
                                value={team.id}
                              />
                              <div className="flex items-center space-x-2">
                                <div className="flex items-center justify-center w-8 h-6 rounded bg-project/10 -my-[2px]">
                                  {team.thumbnail_url && (
                                    <img src={team.thumbnail_url} className="block object-cover w-full h-full rounded" />
                                  )}
                                  {!team.thumbnail_url && charity.logo_url && (
                                    <img src={charity.logo_url} className="block w-1/4 opacity-50" />
                                  )}
                                </div>
                                <span>
                                  {team.title}
                                </span>
                              </div>
                            </RadioLabel>
                          ))}
                        </div>
                      )}
                      {!!params.teams_search && teams.length === 0 && (
                        <div className="rounded-md bg-slate-50 p-3 text-slate-500 mt-2">
                          {t('frontend:no_results')}
                        </div>
                      )}
                    </>
                  )}
                  {selectedTeam && (
                    <CheckboxLabel className="px-3 py-2 mt-2 border rounded-md border-slate-250">
                      <Checkbox {...register('team_id', { onChange: () => setSelectedTeam(null) })} />
                      <div className="flex items-center space-x-2">
                        <div className="flex items-center justify-center w-8 h-6 rounded bg-project/10 -my-[2px]">
                          {selectedTeam.thumbnail_url && (
                            <img src={selectedTeam.thumbnail_url} className="block object-cover w-full h-full rounded" />
                          )}
                          {!selectedTeam.thumbnail_url && charity.logo_url && (
                            <img src={charity.logo_url} className="block w-1/4 opacity-50" />
                          )}
                        </div>
                        <span>
                          {selectedTeam.title}
                        </span>
                      </div>
                    </CheckboxLabel>
                  )}
                  <ErrorMessage error={errors.team_id || errors.teams_search} attribute={t('frontend:team')} />
                </InputGroup>
              )}
            />
          </>
        )}

        <FadeIn
          when={!joinTeam}
          render={() => (
            <InputGroup>
              <InputLabel required valid={!errors.title} htmlFor="edit_campaign_title">
                {t('frontend:campaign_title')}
              </InputLabel>
              <Input
                {...register('title', { required: true })}
                id="edit_campaign_title"
                inputSize="lg"
                className="text-base font-medium"
                aria-invalid={!!errors.title}
                disabled={campaign.locked_fields?.includes('title')}
              />
              <ErrorMessage error={errors.title} attribute={t('frontend:campaign_title')} />
            </InputGroup>
          )}
        />

        <InputGroup>
          <InputLabel htmlFor="edit_campaign_image">
            {t('frontend:campaign_image')}
          </InputLabel>
          <InputDescription>
            {t('frontend:campaign_image_description')}
          </InputDescription>
          <div className="p-4 border rounded-md border-slate-250">
            <div
              className="flex items-center justify-center mb-4 transition-all rounded-sm bg-slate-500 aspect-video"
            >
              {!image && !deleteImage && campaign.image_url && (
                <img
                  src={campaign.image_url}
                  alt={campaign.title}
                  className="object-cover w-full h-full rounded-sm"
                />
              )}
              {image && (
                <ImageCropper
                  image={image}
                  onChange={handleCropChange}
                  aspect={16 / 9}
                />
              )}
              {((!image && !campaign.image_url) || deleteImage) && (
                <Icon className="!w-12 !h-12 text-slate-400">
                  <Image strokeWidth={1} />
                </Icon>
              )}
            </div>
            <div className="flex space-x-2">
              <FileInput
                {...register('image')}
                accept=".jpg,.jpeg,.png"
                id="edit_campaign_image"
                aria-invalid={!!errors.image}
                onChange={handleImageChange}
              >
                <Icon className="mr-2">
                  <Image />
                </Icon>
                {t('shared:form.select')}
              </FileInput>
              {canDeleteImage && (
                <Button onClick={() => resetImage()} variant="tertiary" size="sm">
                  <Icon className="mr-2">
                    <Trash />
                  </Icon>
                  {t('shared:form.delete')}
                </Button>
              )}
            </div>
          </div>
          <ErrorMessage error={errors.image} attribute={t('frontend:campaign_image')} />
        </InputGroup>

        <FadeIn
          when={allowStartDate}
          render={() => (
            <InputGroup>
              <InputLabel
                required={project.required_fields.includes(CampaignField.StartDate)}
                valid={!errors.start_date}
                htmlFor="edit_campaign_start_date"
              >
                {t('frontend:start_date')}
              </InputLabel>
              <InputDescription>
                {t('frontend:campaign_start_date_description')}
              </InputDescription>
              <Controller
                control={control}
                name="start_date"
                rules={{
                  required: project.required_fields.includes(CampaignField.StartDate),
                  max: endDate || undefined,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DateInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ariaInvalid={!!errors.start_date}
                    optional={!project.required_fields.includes(CampaignField.StartDate)}
                    endDate={endDate}
                    selectsStart
                    id="edit_campaign_start_date"
                  />
                )}
              />
              <ErrorMessage
                error={errors.start_date}
                attribute={t('frontend:start_date')}
                values={{ max: t('frontend:end_date') }}
              />
            </InputGroup>
          )}
        />

        <FadeIn
          when={allowEndDate}
          render={() => (
            <InputGroup>
              <InputLabel valid={!errors.end_date} htmlFor="edit_campaign_end_date">
                {t('frontend:end_date')}
              </InputLabel>
              <InputDescription>
                {t('frontend:campaign_end_date_description')}
              </InputDescription>
              <Controller
                control={control}
                name="end_date"
                rules={{
                  required: project.required_fields.includes(CampaignField.EndDate),
                  min: startDate ? formatDate(startDate) : undefined,
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <DateInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ariaInvalid={!!errors.end_date}
                    optional={!project.required_fields.includes(CampaignField.EndDate)}
                    startDate={startDate}
                    selectsEnd
                    id="edit_campaign_end_date"
                  />
                )}
              />
              <ErrorMessage
                error={errors.end_date}
                attribute={t('frontend:end_date')}
                values={{ min: t('frontend:start_date') }}
              />
            </InputGroup>
          )}
        />

        {project.fields.includes(CampaignField.FundraisingTarget) && (
          <InputGroup>
            <InputLabel
              required={project.required_fields.includes(CampaignField.FundraisingTarget)}
              valid={!errors.fundraising_target}
              htmlFor="edit_campaign_fundraising_target"
            >
              {t('frontend:fundraising_target')}
            </InputLabel>
            <InputDescription>
              {t('frontend:fundraising_target_description', { charity: charity.title })}
            </InputDescription>
            <InputWrapper>
              <label htmlFor="edit_campaign_fundraising_target">
                    €
              </label>
              <Input
                {...register('fundraising_target', {
                  required: project.required_fields.includes(CampaignField.FundraisingTarget),
                })}
                id="edit_campaign_fundraising_target"
                className="pl-7"
                onBlur={(event) => {
                  handleCurrencyBlur(event);
                  setValue('fundraising_target', event.target.value);
                }}
                aria-invalid={!!errors.fundraising_target}
                min="0"
              />
            </InputWrapper>
            <ErrorMessage error={errors.fundraising_target} attribute={t('frontend:fundraising_target')} />
          </InputGroup>
        )}

        <InputGroup>
          <InputLabel htmlFor="edit_campaign_description">
            {t('frontend:campaign_description')}
          </InputLabel>
          <InputDescription>
            {t('frontend:campaign_description_description')}
          </InputDescription>
          <Textarea
            {...register('description')}
            id="edit_campaign_description"
            aria-invalid={!!errors.description}
            placeholder={campaign.type === CampaignType.Team
              ? (project.default_team_description || charity.default_team_description)
              : (project.default_individual_description || charity.default_individual_description)}
            rows={12}
          />
          <ErrorMessage error={errors.description} attribute={t('frontend:campaign_description')} />
        </InputGroup>

        <InputGroup>
          <InputLabel htmlFor="edit_campaign_donation_message">
            {t('frontend:donation_success_message')}
          </InputLabel>
          <InputDescription>
            {t('frontend:donation_success_message_description')}
          </InputDescription>
          <Textarea
            {...register('donation_message')}
            id="edit_campaign_donation_message"
            aria-invalid={!!errors.donation_message}
            rows={6}
          />
          <ErrorMessage error={errors.donation_message} attribute={t('frontend:donation_success_message')} />
        </InputGroup>

        {!project.private && project.type === ProjectType.DonationBox && (
          <InputGroup>
            <InputLabel htmlFor="edit_campaign_private">
              {t('frontend:visibility')}
            </InputLabel>
            <CheckboxLabel>
              <Checkbox {...register('private')}/>
              <span>
                {t('frontend:campaign_private_label')}
              </span>
            </CheckboxLabel>
          </InputGroup>
        )}
      </div>

      <hr />

      <div className="space-y-6">
        <h4 className="font-medium text-slate-500">
          {t('frontend:your_details')}
        </h4>

        <div className="grid grid-cols-2 gap-6">
          <InputGroup>
            <InputLabel required valid={!errors.first_name} htmlFor="edit_campaign_first_name">
              {t('frontend:first_name')}
            </InputLabel>
            <Input
              {...register('first_name', { required: true })}
              id="edit_campaign_first_name"
              aria-invalid={!!errors.first_name}
              disabled={campaign.locked_fields?.includes('user')}
            />
            <ErrorMessage attribute={t('frontend:first_name')} error={errors.first_name }/>
          </InputGroup>

          <InputGroup>
            <InputLabel required valid={!errors.last_name} htmlFor="edit_campaign_last_name">
              {t('frontend:last_name')}
            </InputLabel>
            <Input
              {...register('last_name', { required: true })}
              id="edit_campaign_last_name"
              aria-invalid={!!errors.last_name}
              disabled={campaign.locked_fields?.includes('user')}
            />
            <ErrorMessage attribute={t('frontend:last_name')} error={errors.last_name }/>
          </InputGroup>
        </div>

        <InputGroup>
          <InputLabel required valid={!errors.email} htmlFor="edit_campaign_email">
            {t('frontend:email_address')}
          </InputLabel>
          <Input
            {...register('email', { required: true })}
            inputMode="email"
            id="edit_campaign_email"
            aria-invalid={!!errors.email}
            disabled={campaign.locked_fields?.includes('user')}
          />
          <ErrorMessage attribute={t('frontend:email_address')} error={errors.email }/>
          {email !== campaign.user.email && (
            <InfoMessage variant="alert" className="mt-4">
              {t('frontend:edit_email_message')}
            </InfoMessage>
          )}
        </InputGroup>

        {project.fields.includes(CampaignField.Gender) && (
          <InputGroup>
            <InputLabel
              required={project.required_fields.includes(CampaignField.Gender)}
              valid={!errors.gender}
              htmlFor="edit_campaign_gender"
            >
              {t('frontend:gender')}
            </InputLabel>
            <div className="flex space-x-3">
              <RadioLabel>
                <Radio
                  {...register('gender', { required: project.required_fields.includes(CampaignField.Gender) })}
                  value={Gender.Male}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.male')}
                </span>
              </RadioLabel>
              <RadioLabel>
                <Radio
                  {...register('gender', { required: project.required_fields.includes(CampaignField.Gender) })}
                  value={Gender.Female}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.female')}
                </span>
              </RadioLabel>
              <RadioLabel>
                <Radio
                  {...register('gender', { required: project.required_fields.includes(CampaignField.Gender) })}
                  value={Gender.Nonbinary}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.nonbinary')}
                </span>
              </RadioLabel>
              {gender && !project.required_fields.includes(CampaignField.Gender) && (
                <Button
                  onClick={(event) => {
                    event.preventDefault(); setValue('gender', null);
                  }}
                  variant="tertiary"
                  size="xs"
                  aria-label={t('shared:filter.clear')}
                >
                  <Icon>
                    <XCircle />
                  </Icon>
                </Button>
              )}
            </div>
            <ErrorMessage error={errors.gender} attribute={t('frontend:gender')} />
          </InputGroup>
        )}

        {project.fields.includes(CampaignField.DateOfBirth) && (
          <InputGroup>
            <InputLabel
              required={project.required_fields.includes(CampaignField.DateOfBirth)}
              valid={!errors.date_of_birth}
              htmlFor="edit_campaign_date_of_birth"
            >
              {t('frontend:date_of_birth')}
            </InputLabel>
            <Input
              {...register('date_of_birth', { required: project.required_fields.includes(CampaignField.DateOfBirth) })}
              onBlur={(event) => {
                handleDateBlur(event);
                setValue('date_of_birth', event.target.value);
              }}
              id="edit_campaign_date_of_birth"
              placeholder={t('shared:date_input.date_format_placeholder')}
              aria-invalid={!!errors.date_of_birth}
            />
            <ErrorMessage error={errors.date_of_birth} attribute={t('frontend:date_of_birth')} />
          </InputGroup>
        )}

        <FadeIn
          when={allowCompanyName}
          className="space-y-6"
          render={() => (
            <>
              {!project.required_fields.includes(CampaignField.CompanyName) && (
                <InputGroup>
                  <CheckboxLabel>
                    <Checkbox
                      checked={isCompany}
                      onChange={toggleCompany}
                      id="edit_campaign_company_toggle"
                      className="mt-1"
                    />
                    <span>
                      {t('frontend:create_company_campaign')}
                    </span>
                  </CheckboxLabel>
                </InputGroup>
              )}
              <FadeIn
                when={isCompany}
                render={() => (
                  <InputGroup>
                    <InputLabel
                      required={isCompany}
                      valid={!errors.company_name}
                      htmlFor="edit_campaign_company_name"
                    >
                      {t('frontend:company_name')}
                    </InputLabel>
                    <Input
                      {...register('company_name', {
                        required: isCompany,
                      })}
                      id="edit_campaign_company_name"
                      aria-invalid={!!errors.company_name}
                    />
                    <ErrorMessage error={errors.company_name} attribute={t('frontend:company_name')} />
                  </InputGroup>
                )}
              />
            </>
          )}
        />

        {project.fields.includes(CampaignField.Phone) && (
          <InputGroup>
            <InputLabel
              required={project.required_fields.includes(CampaignField.Phone)}
              valid={!errors.phone}
              htmlFor="edit_campaign_phone"
            >
              {t('frontend:phone_number')}
            </InputLabel>
            <Controller
              control={control}
              name="phone"
              rules={{
                required: project.required_fields.includes(CampaignField.Phone),
              }}
              render={({ field: { onChange, onBlur, value } }) => (
                <PhoneNumberInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  country={campaign.country}
                  id="edit_campaign_phone"
                  ariaInvalid={!!errors.phone}
                />
              )}
            />
            <ErrorMessage error={errors.phone} attribute={t('frontend:phone_number')} />
          </InputGroup>
        )}

        {project.fields.includes(CampaignField.Address) && (
          <AddressInputs form={form} required={project.required_fields.includes(CampaignField.Address)} />
        )}

        {project.custom_fields.map((field, index) => (
          <CustomFieldInput
            form={form as unknown as CustomFieldInputFormApi}
            index={index}
            field={field}
            key={field.id}
          />
        ))}
      </div>

      <hr />

      <div className="space-y-6">
        <h4 className="font-medium text-slate-500">
          {t('frontend:mails')}
        </h4>

        <ScrollIntoView when={scrollToEmailSettings}>
          <InputGroup>
            <InputLabel valid={!errors.email_opt_in} htmlFor="campaign_email_opt_in">
              {t('frontend:email_notifications')}
            </InputLabel>
            <CheckboxLabel>
              <Checkbox
                {...register('email_opt_in')}
                id="campaign_email_opt_in"
              />
              <span>
                {t('frontend:campaign_email_opt_in_text')}
              </span>
            </CheckboxLabel>
          </InputGroup>
        </ScrollIntoView>
      </div>

      <hr />

      <div ref={stickyFooter.anchorRef} className="!mt-0">
        <div
          ref={stickyFooter.elementRef}
          className={classNames(isDirty && stickyFooter.isSticky && 'fixed z-20 w-full bg-white border-t')}
          style={isDirty && stickyFooter.isSticky ? stickyFooter.position : {}}
        >
          <div className={classNames(isDirty && stickyFooter.isSticky ? 'max-w-2xl mx-auto px-4 sm:px-8 py-3' : 'pt-8 sm:pt-10')}>
            <Button
              type="submit"
              loading={isSubmitting}
              className="block w-full"
            >
              {t('shared:form.save')}
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
